import React, { useEffect, useMemo, useState } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormGroup,
    FormControlLabel,
    Checkbox,
    TextField,
    Box,
    Typography
} from '@mui/material'
import { ISignedURL } from '../../definitions/Upload'
import VectorIcon from '../icons/VectorIcon'

interface P {
    open: boolean
    onClose: () => void
    onConfirm: () => void
    pdfs: ISignedURL[]
    selected: string[]
    onChange: (selected: string[]) => void
    fileNameMap: Map<string, string>
}

export default function PdfSelectModal({
    open,
    onClose,
    pdfs,
    selected,
    onChange,
    onConfirm,
    fileNameMap
}: P) {
    const [search, setSearch] = useState('')
    useEffect(() => {
        if (open) setSearch('')
    }, [open])

    // Filter based on real data (file name)
    const filteredPdfs = useMemo(() => {
        return pdfs?.filter(pdf =>
            pdf.url?.toLowerCase().includes(search.toLowerCase())
        ) || []
    }, [pdfs, search])

    // Toggle single checkbox
    const handleToggle = (key: string) => {
        const newSelected = selected.includes(key)
            ? selected.filter(id => id !== key)
            : [...selected, key]

        onChange(newSelected)

        console.log('Updated selection IDs:', newSelected)
        console.log(
            'Selected PDFs:',
            pdfs.filter(pdf => newSelected.includes(pdf.key))
        )
    }

    // Select all (filtered only)
    const handleSelectAll = (checked: boolean) => {
        let newSelected: string[]

        const filteredKeys = filteredPdfs.map(p => p.key)

        if (checked) {
            newSelected = Array.from(new Set([...selected, ...filteredKeys]))
        } else {
            newSelected = selected.filter(id => !filteredKeys.includes(id))
        }

        onChange(newSelected)

        console.log('Select All toggled:', checked)
        console.log('Updated selection IDs:', newSelected)
        console.log(
            'Selected PDFs:',
            pdfs.filter(pdf => newSelected.includes(pdf.key))
        )
    }

    const getFileName = (pdf: ISignedURL) => {
        return fileNameMap.get(pdf.key)
            || pdf.url?.split('?')[0].split('/').pop()
            || 'Unknown file'
    }

    const allFilteredSelected
        = filteredPdfs.length > 0 &&
            filteredPdfs.every(pdf => selected.includes(pdf.key))

    const someFilteredSelected
        = filteredPdfs.some(pdf => selected.includes(pdf.key)) &&
            !allFilteredSelected

    const handleSubmit = () => {
        const selectedFiles = pdfs.filter(pdf =>
            selected.includes(pdf.key)
        )

        console.log('FINAL SELECTED FILES:')
        console.table(selectedFiles)

        onConfirm()
    }

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth='sm'>
            <DialogTitle>
                Select PDF Files ({selected.length} selected)
            </DialogTitle>

            <DialogContent dividers>
                <TextField
                    fullWidth
                    size='small'
                    placeholder='Search PDFs...'
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    sx={{ mb: 2 }}
                />

                <FormControlLabel
                    control={(
                        <Checkbox
                            checked={allFilteredSelected}
                            indeterminate={someFilteredSelected}
                            onChange={e => handleSelectAll(e.target.checked)}
                        />
                    )}
                    label='Select All (filtered)'
                />

                <Box sx={{ maxHeight: 300, overflowY: 'auto', mt: 1 }}>
                    <FormGroup>
                        {filteredPdfs.length > 0
                            ? (
                                    filteredPdfs.map(pdf => (
                                        <FormControlLabel
                                            key={pdf.key}
                                            control={(
                                                <Checkbox
                                                    checked={selected.includes(pdf.key)}
                                                    onChange={() => handleToggle(pdf.key)}
                                                />
                                            )}
                                            label={getFileName(pdf)}
                                        />
                                    ))
                                )
                            : (
                                    <Typography variant='body2' color='text.secondary'>
                                        No PDFs found
                                    </Typography>
                                )}
                    </FormGroup>
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>

                <Button
                    startIcon={<VectorIcon />}
                    variant='contained'
                    disabled={selected.length === 0}
                    onClick={handleSubmit}>
                    Run AI
                </Button>
            </DialogActions>
        </Dialog>
    )
}
